import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { logout } from '../actions/authActions';
import { browserHistory } from '@browserHistory';
import { getRedirectPath } from '../utils/helpers';

const FeaturesContext = createContext();

export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};

export const FeaturesProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);
  const [isFeaturesLoading, setIsFeaturesLoading] = useState(true);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const isMountedRef = useRef(true);

  const getFeatures = async () => {
    try {
      if (isMountedRef.current) {
        setIsFeaturesLoading(true);
      }
      const response = await axios.get('/api/admin/features');
      if (response.status === 200 && isMountedRef.current) {
        setFeatures(response.data?.features || []);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setError(error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsFeaturesLoading(false);
      }
    }
  };

  useEffect(() => {
    if (user?.roles && user?.roles.length > 0) {
      getFeatures();
    } else if (user?.roles && user?.roles.length === 0) {
      setFeatures([]);
      setIsFeaturesLoading(false);
    }
  }, [user]); // Run only once when provider mounts
  
  const clearUser = () => {
    if (isMountedRef.current) {
      setUser(null);
      setFeatures([]);
      setIsFeaturesLoading(false);
      setError(null);
    }
  };

  const getUser = async () => {
    try {
      if (isMountedRef.current) {
        setIsUserLoading(true);
      }
      const response = await axios.get('/api/admin/user/roles');
      if (response.status === 200 && isMountedRef.current) {
        setUser(response.data?.user);
      }
    } catch (error) {
      if (isMountedRef.current) {
        if (error.response?.status === 401) {
          clearUser();
          logout()().then(() => {
            const redirectPath = getRedirectPath(window.location.pathname);
            browserHistory.push('/login?redirect=' + encodeURIComponent(redirectPath));
          });
        } else {
          setError(error);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsUserLoading(false);
      }
    }
  };
  useEffect(() => {
    getUser();
  }, []);

  // Set up axios interceptor to handle 401 responses globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && isMountedRef.current) {
          clearUser();
          logout()().then(() => {
            const redirectPath = getRedirectPath(window.location.pathname);
            browserHistory.push('/login?redirect=' + encodeURIComponent(redirectPath));
          });
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup effect to prevent state updates after unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const retryFeatures = async () => {
    try {
      
      if (isMountedRef.current) {
        setIsRetrying(true);
        setError(null);
        setIsFeaturesLoading(true);
      }
      const response = await axios.get('/api/admin/features');
      if (response.status === 200 && isMountedRef.current) {
        setFeatures(response.data?.features || []);
        setError(null);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setError(error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsFeaturesLoading(false);
        setIsRetrying(false);
      }
    }
  };

  const value = {
    features,
    isFeaturesLoading,
    isUserLoading,
    error,
    user,
    setUser,
    clearUser,
    retryFeatures,
    isRetrying
  };

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
};
